import type * as Y from "yjs";
import type { WebsocketProvider } from "y-websocket";
import type { Room } from "trystero";
import * as awarenessProtocol from "y-protocols/awareness";
import * as syncProtocol from "y-protocols/sync";
import * as encoding from "lib0/encoding";
import * as decoding from "lib0/decoding";

import { Awareness } from "y-protocols/awareness";

/**
 * Transport abstraction for session sync.
 *
 * Two transports, one interface, because the right answer depends on how many
 * people are in the session:
 *
 *   - WebSocket (star topology): one relay, N connections. Scales to a full
 *     33-player team without effort. Needs a server.
 *   - P2P mesh (WebRTC): no server, but every peer connects to every other
 *     peer. At 33 players that is 528 connections, which melts phones. Fine
 *     for a squad, wrong for a team.
 *
 * The application never learns which one it is using, so the choice can change
 * without touching a single component.
 */

export type SyncStatus = "disconnected" | "connecting" | "connected" | "reconnecting" | "error";

export interface SyncState {
  readonly status: SyncStatus;
  /** Other participants currently connected. Excludes the local client. */
  readonly peerCount: number;
  readonly error: string | null;
}

export type SyncListener = (state: SyncState) => void;

export interface SyncProvider {
  readonly awareness: Awareness;
  connect(): Promise<void>;
  disconnect(): void;
  subscribe(listener: SyncListener): () => void;
  getState(): SyncState;
}

/**
 * Peer ceiling for the mesh transport. Past this, a mesh costs more in
 * connections than it saves in infrastructure.
 */
export const MESH_PEER_LIMIT = 10;

export interface ProviderOptions {
  readonly roomName: string;
  readonly doc: Y.Doc;
  /** WebSocket relay endpoint. When absent, the mesh transport is used. */
  readonly websocketUrl?: string | undefined;
}

/**
 * Shared plumbing: listener fan-out and state bookkeeping, so each transport
 * only has to implement its own connect and disconnect.
 */
abstract class BaseSyncProvider implements SyncProvider {
  readonly awareness: Awareness;
  protected readonly doc: Y.Doc;
  protected readonly roomName: string;

  private readonly listeners = new Set<SyncListener>();
  private state: SyncState = { status: "disconnected", peerCount: 0, error: null };

  constructor(options: ProviderOptions) {
    this.doc = options.doc;
    this.roomName = options.roomName;
    this.awareness = new Awareness(options.doc);
  }

  abstract connect(): Promise<void>;

  disconnect(): void {
    this.setState({ status: "disconnected", peerCount: 0, error: null });
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    // Fire immediately so a late subscriber is never stuck on a stale value.
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): SyncState {
    return this.state;
  }

  protected setState(next: Partial<SyncState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) listener(this.state);
  }

  protected countPeers(): number {
    // Awareness includes the local client; the UI wants everyone else.
    return Math.max(0, this.awareness.getStates().size - 1);
  }
}

/**
 * WebSocket transport, backed by y-websocket.
 *
 * y-websocket already speaks the sync and awareness protocols and handles
 * reconnection with backoff, so this is mostly wiring status through.
 */
export class WebsocketSyncProvider extends BaseSyncProvider {
  private provider: WebsocketProvider | null = null;
  private readonly url: string;

  constructor(options: ProviderOptions & { websocketUrl: string }) {
    super(options);
    this.url = options.websocketUrl;
  }

  async connect(): Promise<void> {
    if (this.provider) return;
    this.setState({ status: "connecting", error: null });

    const { WebsocketProvider } = await import("y-websocket");
    const provider = new WebsocketProvider(this.url, this.roomName, this.doc, {
      connect: true,
      awareness: this.awareness,
    });
    this.provider = provider;

    provider.on("status", (event: { status: string }) => {
      if (event.status === "connected") {
        this.setState({ status: "connected", peerCount: this.countPeers(), error: null });
      } else if (event.status === "connecting") {
        this.setState({ status: "connecting" });
      } else {
        this.setState({ status: "reconnecting", peerCount: 0 });
      }
    });

    provider.on("connection-error", () => {
      this.setState({ status: "error", error: "Relay unreachable" });
    });

    this.awareness.on("change", () => {
      this.setState({ peerCount: this.countPeers() });
    });
  }

  override disconnect(): void {
    this.provider?.destroy();
    this.provider = null;
    super.disconnect();
  }
}

/**
 * Peer-to-peer transport over Trystero, for running without any server.
 *
 * Two deliberate fixes over the equivalent code in SubCoach:
 *
 *   1. Remote updates are applied with a sentinel transaction origin, and the
 *      broadcast guard checks for it. SubCoach guards on the string "remote"
 *      but never sets it, so every peer rebroadcasts everything it receives —
 *      harmless at two peers, quadratic past that.
 *   2. Awareness state for departed peers is cleared on leave, so the roster
 *      does not fill up with ghosts.
 */
export class P2PSyncProvider extends BaseSyncProvider {
  private room: Room | null = null;
  private docUpdateHandler: ((update: Uint8Array, origin: unknown) => void) | null = null;
  private awarenessUpdateHandler:
    ((changes: { added: number[]; updated: number[]; removed: number[] }) => void) | null =
    null;

  /** Sentinel marking updates that arrived from a peer and must not echo back. */
  private readonly remoteOrigin = Symbol("remote");
  private readonly peerClients = new Map<string, number>();

  async connect(): Promise<void> {
    if (this.room) return;
    this.setState({ status: "connecting", error: null });

    // Only the transport is loaded lazily. The Yjs protocol helpers are tiny
    // and already in the main bundle via y-websocket, so importing them
    // dynamically would split nothing and only add a await.
    const { joinRoom } = await import("trystero/torrent");

    const room = joinRoom(
      {
        appId: "wardogs-command",
        // Trystero's defaults include trackers that no longer resolve or refuse
        // the WebSocket upgrade, which produces a stream of console errors and
        // slows discovery while the browser waits on them. Listing relays
        // explicitly keeps both peers announcing to the same working set.
        relayUrls: ["wss://tracker.webtorrent.dev", "wss://tracker.openwebtorrent.com"],
        rtcConfig: {
          iceServers: [
            { urls: "stun:stun.l.google.com:19302" },
            { urls: "stun:stun1.l.google.com:19302" },
            { urls: "stun:global.stun.twilio.com:3478" },
          ],
        },
      },
      this.roomName,
    );
    this.room = room;

    const [sendSync, getSync] = room.makeAction<Uint8Array>("sync");
    const [sendAwareness, getAwareness] = room.makeAction<Uint8Array>("aware");

    getSync((data, peerId) => {
      try {
        const decoder = decoding.createDecoder(data);
        const encoder = encoding.createEncoder();
        // The sentinel origin is what stops the echo storm.
        syncProtocol.readSyncMessage(decoder, encoder, this.doc, this.remoteOrigin);
        if (encoding.length(encoder) > 0) {
          void sendSync(encoding.toUint8Array(encoder), peerId);
        }
      } catch (error) {
        this.setState({
          status: "error",
          error: error instanceof Error ? error.message : "Sync failed",
        });
      }
    });

    getAwareness((data, peerId) => {
      try {
        awarenessProtocol.applyAwarenessUpdate(this.awareness, data, peerId);
        this.setState({ peerCount: this.countPeers() });
      } catch {
        // A malformed awareness frame is not worth tearing the session down.
      }
    });

    this.docUpdateHandler = (update, origin) => {
      if (origin === this.remoteOrigin) return;
      const encoder = encoding.createEncoder();
      syncProtocol.writeUpdate(encoder, update);
      void sendSync(encoding.toUint8Array(encoder));
    };
    this.doc.on("update", this.docUpdateHandler);

    this.awarenessUpdateHandler = ({ added, updated }) => {
      const changed = added.concat(updated);
      if (changed.length === 0) return;
      void sendAwareness(awarenessProtocol.encodeAwarenessUpdate(this.awareness, changed));
    };
    this.awareness.on("update", this.awarenessUpdateHandler);

    room.onPeerJoin((peerId) => {
      const encoder = encoding.createEncoder();
      syncProtocol.writeSyncStep1(encoder, this.doc);
      void sendSync(encoding.toUint8Array(encoder), peerId);
      void sendAwareness(
        awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID]),
      );
      this.setState({ status: "connected", peerCount: this.countPeers(), error: null });
    });

    room.onPeerLeave((peerId) => {
      // Clear the departed peer's awareness so the roster does not keep ghosts.
      const clientId = this.peerClients.get(peerId);
      if (clientId !== undefined) {
        awarenessProtocol.removeAwarenessStates(this.awareness, [clientId], "peer left");
        this.peerClients.delete(peerId);
      }
      this.setState({ peerCount: this.countPeers() });
    });

    this.setState({ status: "connected", peerCount: this.countPeers() });
  }

  override disconnect(): void {
    if (this.docUpdateHandler) {
      this.doc.off("update", this.docUpdateHandler);
      this.docUpdateHandler = null;
    }
    if (this.awarenessUpdateHandler) {
      this.awareness.off("update", this.awarenessUpdateHandler);
      this.awarenessUpdateHandler = null;
    }
    void this.room?.leave();
    this.room = null;
    this.peerClients.clear();
    super.disconnect();
  }
}

/** Build the right transport for the current configuration. */
export function createSyncProvider(options: ProviderOptions): SyncProvider {
  if (options.websocketUrl) {
    return new WebsocketSyncProvider({ ...options, websocketUrl: options.websocketUrl });
  }
  return new P2PSyncProvider(options);
}
